'use client';

import { useParams } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import {
  createUser,
  Customer,
  getCustomerDetail,
  getCustomerForgotPw,
  getCustomerResetPw,
  getUserPaymentInfo,
  userLogin
} from '@/apis/tds';

export type LoginInfo = Customer & {
  identity?: number;
};

export type GuestInfo = {
  email: string;
  firstName: string;
  lastName: string;
  mobile: string;
  phone: string;
};

export type StoredCards = {
  accountNumber: string;
  address2: string;
  address: string;
  cardType: string;
  cellPhone: string;
  city: string;
  country: string;
  customer: string | null;
  expirationMonth: string;
  expirationYear: string;
  firstName: string;
  firstSix: string;
  lastFour: string;
  lastName: string;
  lastUpdateTime: string | null;
  phone: string | null;
  state: string;
  storedPaymentId: number;
  zip: string;
}[];

interface LoginContextProps {
  authorizeUser: (data: { [key: string]: string }) => Promise<LoginInfo | null>;
  createUserRequest: (data: { [key: string]: any }) => Promise<{ [key: string]: any } | null>;
  forgotPasswordRequest: (data: { [key: string]: string }) => Promise<boolean | null>;
  resetPasswordRequest: (data: { [key: string]: string }) => Promise<boolean | null>;
  newCardsRequested: () => Promise<void | null>;
  guestInfo: GuestInfo | null;
  loginInfo: LoginInfo | null;
  purchaseLoginStep: boolean | null;
  storedCards: StoredCards | null;
  setPurchaseLoginStep: React.Dispatch<React.SetStateAction<boolean | null>>;
  setGuestInfo: React.Dispatch<React.SetStateAction<GuestInfo | null>>;
  setLoginInfo: React.Dispatch<React.SetStateAction<LoginInfo | null>>;
  saveCard: boolean;
  setSaveCard: React.Dispatch<React.SetStateAction<boolean>>;
  userCheckedOut: boolean;
  setUserCheckedOut: React.Dispatch<React.SetStateAction<boolean>>;
  loader: boolean;
}

const LoginContext = createContext<LoginContextProps | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

const useInitialState = () => {
  const [loginInfo, setLoginInfo] = useState<LoginInfo | null>(null);
  const [guestInfo, setGuestInfo] = useState<GuestInfo | null>(null);
  const [purchaseLoginStep, setPurchaseLoginStep] = useState<boolean | null>(null);
  const [storedCards, setStoredCards] = useState<StoredCards | null>(null);
  const [saveCard, setSaveCard] = useState<boolean>(false);
  const [userCheckedOut, setUserCheckedOut] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  return {
    authorizeUser: async (data: { [key: string]: string }): Promise<LoginInfo | null> => null,
    createUserRequest: async (data: { [key: string]: any }): Promise<{ [key: string]: any } | null> => null,
    forgotPasswordRequest: async (data: { [key: string]: string }): Promise<boolean | null> => null,
    resetPasswordRequest: async (data: { [key: string]: string }): Promise<boolean | null> => null,
    newCardsRequested: async (): Promise<void | null> => null,
    guestInfo,
    loginInfo,
    storedCards,
    purchaseLoginStep,
    setGuestInfo,
    setLoginInfo,
    setPurchaseLoginStep,
    saveCard,
    setSaveCard,
    userCheckedOut,
    setUserCheckedOut,
    loader,
    setLoader
  };
};

export const LoginProvider: React.FC<ProviderProps> = ({ children }) => {
  const [loginInfo, setLoginInfo] = useState<LoginInfo | null>(null);
  const [guestInfo, setGuestInfo] = useState<GuestInfo | null>(null);
  const [purchaseLoginStep, setPurchaseLoginStep] = useState<boolean | null>(null);
  const [storedCards, setStoredCards] = useState<StoredCards | null>(null);
  const [saveCard, setSaveCard] = useState<boolean>(false);
  const [userCheckedOut, setUserCheckedOut] = useState<boolean>(false);
  const [isReady, setIsReady] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);

  const { lng }: any = useParams();

  const setState = (key: string, setter: React.Dispatch<React.SetStateAction<any>>) => {
    const dataFromStorage = sessionStorage.getItem(key);
    if (dataFromStorage) {
      try {
        setter(JSON.parse(dataFromStorage));
      } catch (e) {}
    }
  };

  useEffect(() => {
    setState('purchaseLoginStep', setPurchaseLoginStep);
    setState('loginInfo', setLoginInfo);
    setState('guestInfo', setGuestInfo);
    setState('storedCards', setStoredCards);
    setState('saveCard', setSaveCard);
    setState('userCheckedOut', setUserCheckedOut);
    setIsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setData = (key: string, data: any) => {
    if (isReady) {
      sessionStorage.setItem(key, JSON.stringify(data));
    }
  };

  useEffect(() => {
    setData('purchaseLoginStep', purchaseLoginStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseLoginStep]);

  useEffect(() => {
    setData('loginInfo', loginInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  useEffect(() => {
    setData('guestInfo', guestInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestInfo]);

  useEffect(() => {
    setData('storedCards', storedCards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCards]);

  useEffect(() => {
    setData('saveCard', saveCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveCard]);

  useEffect(() => {
    setData('userCheckedOut', userCheckedOut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCheckedOut]);

  const authorizeUser = async (data: { [key: string]: string }) => {
    setLoader(true);
    const loginResponse = await userLogin(data);
    const detailResponse = await getCustomerDetail();

    if (
      (loginResponse.statusCode && loginResponse.statusCode !== 200) ||
      (detailResponse.statusCode && detailResponse.statusCode !== 200)
    ) {
      setLoader(false);
      throw loginResponse.statusCode !== 200 ? loginResponse : detailResponse;
    }

    detailResponse.identity = detailResponse.customerId;
    setLoginInfo(detailResponse);
    setLoader(false);
    return detailResponse;
  };

  const createUserRequest = async (data: { [key: string]: any }) => {
    return await createUser(data);
  };

  const forgotPasswordRequest = async (data: { [key: string]: string }): Promise<boolean | null> => {
    await getCustomerForgotPw(data);
    return true;
  };

  const resetPasswordRequest = async (data: { [key: string]: string }): Promise<boolean | null> => {
    const response = await getCustomerResetPw(data);
    if (response && response.statusCode && response.statusCode !== 200) {
      return false;
    }
    return true;
  };

  const newCardsRequested = async () => {
    let data = (await getUserPaymentInfo()) || [];
    if (Array.isArray(data)) {
      // Some cards are missing first and last name, so filter them out
      data = data.filter((card: any) => card.firstName && card.lastName);
    }
    setStoredCards(data);
  };

  return isReady ? (
    <LoginContext.Provider
      value={{
        authorizeUser,
        createUserRequest,
        forgotPasswordRequest,
        resetPasswordRequest,
        newCardsRequested,
        guestInfo,
        loginInfo,
        storedCards,
        purchaseLoginStep,
        setGuestInfo,
        setLoginInfo,
        setPurchaseLoginStep,
        saveCard,
        setSaveCard,
        userCheckedOut,
        setUserCheckedOut,
        loader
      }}
    >
      {children}
    </LoginContext.Provider>
  ) : null;
};

export const useLogin = (): LoginContextProps => {
  const initialState = useInitialState();
  const context = useContext(LoginContext);
  return context || initialState;
};
